import { useId, useRef, useState } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type { PieceType } from "@/api/types";
import { usePieceTypes } from "@/hooks/usePieceTypes";
import { PieceTypeDialog } from "./PieceTypeDialog";
import "./PieceTypesPanel.css";

const rateFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

/** "$0.50", "$0.0625" — up to four decimals, never rounded to cents (a rounded rate misleads). */
export function formatPieceRate(rate: number): string {
  return rateFormatter.format(rate);
}

type DialogState = { pieceType: PieceType | null } | null;

/**
 * ef-060-6 — the People tab's "Piece types" region: what piece work is paid by the unit, at what
 * rate. Add / Edit / Archive / Restore, plus a Show archived toggle (archived types are hidden,
 * never deleted). A LIST, not a table — the roster stays the page's only `<table>`.
 *
 * State in the DOM: the region carries `data-state` = loading | error | empty | ready; ONE
 * `role="alert"` carries a load failure or a failed Archive/Restore.
 */
export function PieceTypesPanel() {
  const [showArchived, setShowArchived] = useState(false);
  const { types, status, error, refetch } = usePieceTypes(showArchived);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const headingId = useId();
  const toggleId = useId();

  const state = status === "error" ? "error" : types === null ? "loading" : types.length === 0 ? "empty" : "ready";

  function openDialog(pieceType: PieceType | null, trigger: HTMLElement): void {
    triggerRef.current = trigger;
    setDialog({ pieceType });
  }

  function closeDialog(): void {
    setDialog(null);
    // The dialog unmounts with the state change; hand focus back to what opened it.
    const trigger = triggerRef.current;
    requestAnimationFrame(() => trigger?.isConnected && trigger.focus());
  }

  async function change(type: PieceType, run: () => Promise<unknown>, failure: string): Promise<void> {
    if (busyId !== null) return;
    setBusyId(type.id);
    setActionError(null);
    try {
      await run();
      refetch();
    } catch (err) {
      setActionError(problemMessage(err, failure));
    } finally {
      setBusyId(null);
    }
  }

  const alertText = status === "error" ? error ?? "Could not load piece types." : actionError;

  return (
    <section className="piece-types" aria-labelledby={headingId} data-state={state} aria-busy={state === "loading"}>
      <div className="piece-types__head">
        <h2 id={headingId} className="piece-types__title">
          Piece types
        </h2>
        <div className="piece-types__tools">
          <span className="piece-types__toggle">
            <input
              id={toggleId}
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            <label htmlFor={toggleId}>Show archived</label>
          </span>
          <button
            type="button"
            className="btn btn--primary"
            onClick={(e) => openDialog(null, e.currentTarget)}
          >
            Add piece type
          </button>
        </div>
      </div>

      {alertText && (
        <p className="piece-types__error" role="alert">
          {alertText}
        </p>
      )}
      {state === "loading" && <p className="piece-types__message">Loading piece types…</p>}
      {state === "empty" && (
        <p className="piece-types__message">
          No piece types yet. Add one to pay piece work by the unit (head, lb, bale).
        </p>
      )}

      {types !== null && types.length > 0 && (
        <ul className="piece-types__list" aria-label="Piece types">
          {types.map((t) => {
            const archived = Boolean(t.archivedAt);
            const link = t.herdName ? `Herd: ${t.herdName}` : t.groupName ? `Group: ${t.groupName}` : null;
            const nameId = `piece-type-${t.id}`;
            return (
              <li key={t.id} className="piece-types__item" data-archived={archived}>
                <div className="piece-types__main">
                  <span id={nameId} className="piece-types__name">
                    {t.name}
                    {archived && <span className="piece-types__badge">Archived</span>}
                  </span>
                  <span className="piece-types__detail">
                    <span className="piece-types__rate">{`${formatPieceRate(t.rate)} per ${t.unit}`}</span>
                    {link && <> · {link}</>}
                  </span>
                </div>
                <div className="piece-types__actions">
                  {!archived && (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      aria-describedby={nameId}
                      onClick={(e) => openDialog(t, e.currentTarget)}
                    >
                      Edit
                    </button>
                  )}
                  {archived ? (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      aria-describedby={nameId}
                      disabled={busyId !== null}
                      onClick={() => void change(t, () => demoApi.restorePieceType(t.id), "Could not restore this piece type.")}
                    >
                      Restore
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      aria-describedby={nameId}
                      disabled={busyId !== null}
                      onClick={() => void change(t, () => demoApi.archivePieceType(t.id), "Could not archive this piece type.")}
                    >
                      Archive
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {dialog && (
        <PieceTypeDialog
          key={dialog.pieceType?.id ?? "new"}
          pieceType={dialog.pieceType}
          onClose={closeDialog}
          onSaved={refetch}
        />
      )}
    </section>
  );
}
